import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Box,
  Typography,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Collapse,
  Divider,
} from '@mui/material';
import {
  Apps as AppsIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { ApplicationInfo } from '../hooks/useApplicationObservatory';

interface ApplicationRegistryPanelProps {
  applications: ApplicationInfo[];
  compact?: boolean;
  onApplicationClick?: (app: ApplicationInfo) => void;
}

export const ApplicationRegistryPanel: React.FC<ApplicationRegistryPanelProps> = ({
  applications,
  compact = false,
  onApplicationClick,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedApp, setExpandedApp] = useState<string | null>(null);

  const getStatusIcon = (status: ApplicationInfo['status']) => {
    switch (status) {
      case 'published':
        return <CheckCircleIcon color="success" />;
      case 'draft':
        return <EditIcon color="warning" />;
      case 'rebuild_required':
        return <WarningIcon color="error" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <AppsIcon />;
    }
  };

  const getStatusColor = (status: ApplicationInfo['status']): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'published':
        return 'success';
      case 'draft':
        return 'warning';
      case 'rebuild_required':
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const filteredApps = applications.filter(app =>
    app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    app.key.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Card>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AppsIcon />
            <Typography variant="h6">Application Registry</Typography>
            <Chip label={applications.length} size="small" color="primary" />
          </Box>
        }
        action={
          !compact && (
            <TextField
              size="small"
              placeholder="Search applications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 250 }}
            />
          )
        }
      />
      <CardContent>
        {filteredApps.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            No applications found
          </Typography>
        ) : (
          <List sx={{ maxHeight: compact ? 300 : 600, overflow: 'auto' }}>
            {filteredApps.map((app) => (
              <React.Fragment key={app.id}>
                <ListItem
                  sx={{
                    mb: 1,
                    bgcolor: 'background.default',
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  onClick={() => {
                    if (compact) {
                      onApplicationClick?.(app);
                    } else {
                      setExpandedApp(expandedApp === app.id ? null : app.id);
                    }
                  }}
                >
                  <ListItemIcon>
                    {getStatusIcon(app.status)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {app.name}
                        </Typography>
                        <Chip
                          label={app.status.replace('_', ' ')}
                          size="small"
                          color={getStatusColor(app.status)}
                        />
                        {app.version && (
                          <Chip label={`v${app.version}`} size="small" variant="outlined" />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {app.key}
                        </Typography>
                        {!compact && app.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {app.description}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  {!compact && (
                    <IconButton size="small">
                      {expandedApp === app.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  )}
                  {compact && onApplicationClick && (
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onApplicationClick(app); }}>
                      <OpenInNewIcon />
                    </IconButton>
                  )}
                </ListItem>
                
                {!compact && (
                  <Collapse in={expandedApp === app.id} timeout="auto" unmountOnExit>
                    <Box sx={{ pl: 7, pr: 2, pb: 2 }}>
                      <Divider sx={{ my: 1 }} />
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary">
                            Description
                          </Typography>
                          <Typography variant="body2">
                            {app.description || 'No description'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="caption" color="text.secondary">
                            Created
                          </Typography>
                          <Typography variant="body2">
                            {formatDate(app.createdAt)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="caption" color="text.secondary">
                            Updated
                          </Typography>
                          <Typography variant="body2">
                            {formatDate(app.updatedAt)}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                        <Chip
                          label="View Functions"
                          size="small"
                          onClick={() => console.log('View functions for', app.key)}
                          clickable
                        />
                        <Chip
                          label="View Clients"
                          size="small"
                          onClick={() => console.log('View clients for', app.key)}
                          clickable
                        />
                        <Chip
                          label="API Docs"
                          size="small"
                          onClick={() => console.log('View API docs for', app.key)}
                          clickable
                          icon={<OpenInNewIcon />}
                        />
                      </Box>
                    </Box>
                  </Collapse>
                )}
              </React.Fragment>
            ))}
          </List>
        )}

        {/* Summary */}
        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={`${applications.filter(a => a.status === 'published').length} Published`}
            size="small"
            color="success"
            variant="outlined"
          />
          <Chip
            label={`${applications.filter(a => a.status === 'draft').length} Draft`}
            size="small"
            color="warning"
            variant="outlined"
          />
          {applications.filter(a => a.status === 'rebuild_required').length > 0 && (
            <Chip
              label={`${applications.filter(a => a.status === 'rebuild_required').length} Need Rebuild`}
              size="small"
              color="error"
              variant="outlined"
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// Import Grid for the expanded section
import { Grid } from '@mui/material';

export default ApplicationRegistryPanel;

